import { Injectable } from '@angular/core';
import {User} from "./user.service";
import {ActivatedRouteSnapshot} from "@angular/router";
import {RoomService} from "./room.service";
import {map, Observable, of, switchMap} from "rxjs";
import {SubjectService} from "./subject.service";
import { InviteService } from './invite.service';

export type ProfileField = 'SELFIE' | 'AVATAR' | 'BIO' ;

export class ProfileRequirements {

  emailSuffixes: string[] ;
  fields: ProfileField[] ;

  public constructor() {
    this.emailSuffixes = [];
    this.fields = []
  }
}

export function hasValidEmail(user:User, requirements:ProfileRequirements) {

  if (!requirements.emailSuffixes.length)
    return true ;

  for (let suffix of requirements.emailSuffixes) {
    if (user.email.toLowerCase().endsWith(suffix.toLowerCase())) {
      return true ;
    }
  }

  return false ;
}


export function getMissingFields(user: User, requirements:ProfileRequirements): ProfileField[] {

  let missingFields: ProfileField[] = [] ;

  if (requirements.fields.includes('BIO')) {
    if (!user.bio || user.bio.trim().length == 0) {
      missingFields.push('BIO') ;
    }
  }

  if (requirements.fields.includes('SELFIE')) {
    if (!user.profileImage) {
      missingFields.push('SELFIE');
    }
  }

  return missingFields ;

}

function getSubjectId(returnUrl: string): string | undefined {
  const re = /\/subjects?\/(.*)/;
  const match = re.exec(returnUrl);

  if (match)
    return match[1]
  else
    return undefined ;

}

function getRoomId(returnUrl: string): string | undefined {
  const re = /\/rooms?\/(.*)/;
  const match = re.exec(returnUrl);

  if (match)
    return match[1]
  else
    return undefined ;

}

function getInviteId(returnUrl: string): string | undefined {
  const re = /\/invites?\/(.*)/;
  const match = re.exec(returnUrl);

  if (match)
    return match[1]
  else
    return undefined ;

}



@Injectable({
  providedIn: 'root'
})
export class ProfileRequirementsService {

  constructor(
    private subjects: SubjectService,
    private rooms: RoomService,
    private invites: InviteService
  ) { }

  private getRequirementsFromSubjectId(subjectId: string): Observable<ProfileRequirements> {
      return this.subjects.getSubject(subjectId).pipe(
        map(result => {
            return result.requirements ;
          }
        )); 
  }

  private getRequirementsFromRoomId(roomId: string): Observable<ProfileRequirements> {
      return this.rooms.getRoom(roomId).pipe(
        switchMap(result => {
            return this.subjects.getSubject(result.subjectId!)
          }
        ),
        map(subject => {
          return subject.requirements
        })
      );
  }

  private getRequirementsFromInviteId(inviteId: string): Observable<ProfileRequirements> {
    return this.invites.getInvite(inviteId).pipe(
      switchMap(result => {
          return this.subjects.getSubject(result.subject.id!)
        }
      ),
      map(subject => {
        return subject.requirements
      })
    );
}

  getRequirementsFromRoute(route: ActivatedRouteSnapshot): Observable<ProfileRequirements>  {
    const subjectId = route.params['subjectId'] ;
    const roomId = route.params['roomId'] ;
    const inviteId = route.params['inviteId'] ;

    if (subjectId) 
      return this.getRequirementsFromSubjectId(subjectId) ;

    if (roomId)
      return this.getRequirementsFromRoomId(roomId) ;

    if (inviteId)
      return this.getRequirementsFromInviteId(inviteId) ;

    return of(new ProfileRequirements())
  }

  getRequirementsFromReturnUrl(returnUrl: string): Observable<ProfileRequirements> {
    const subjectId = getSubjectId(returnUrl);
    const roomId = getRoomId(returnUrl);
    const inviteId = getInviteId(returnUrl);
    

    if (subjectId) 
      return this.getRequirementsFromSubjectId(subjectId) ;

    if (roomId)
      return this.getRequirementsFromRoomId(roomId) ;

    if (inviteId)
      return this.getRequirementsFromInviteId(inviteId) ;

    return of(new ProfileRequirements())
  }


}
